/**
 * Reach audio feature (order `FEATURE_ORDER.reachAudio`; M3 plan R11, audio-direction §9's M3 row). Reach mode
 * only. Like M2's `m2audio`, it never edits the audio feature: it takes the running `engine` and `music` from the
 * audio feature's default export (both null until the first gesture) and adds the Reach on top:
 * - reach events → sounds and score cues (src/audio/reach/router.ts: region re-key on a relay transit, the BRAAAM
 *   on arrival);
 * - the cruise hum loop, its gain and pitch following `travel.speed`;
 * - the music floor: Climax during a relay reboot, Tension during an interdiction.
 * It does nothing while the audio feature has no engine (events are skipped, as the audio feature does), and
 * nothing while paused. `frame` allocates nothing but the one source node per sound started (engine.ts).
 */
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { LOOP, type AudioEngine } from '../../audio/engine'
import type { MusicSystem } from '../../audio/music'
import { reachAudioConfig, type ReachAudioConfig } from '../../audio/reach/config'
import { createCruiseLoopParams, cruiseLoop, type CruiseLoopParams } from '../../audio/reach/cruise'
import { musicFloorOf } from '../../audio/reach/music'
import { createReachAudioRouter, keyScoreTo, routeReachEvents, skipReachEvents, type ReachAudioRouter } from '../../audio/reach/router'
import type { TuningGroup, TuningParam } from '../../render/contracts'
import audioFeature from '../audio/feature'

/** Where the running audio lives: the audio feature (a fake in tests). */
export interface AudioSource {
  readonly engine: AudioEngine | null
  readonly music: MusicSystem | null
}

export interface ReachAudioFeature extends GameFeature {
  readonly config: ReachAudioConfig
  /** The live cruise-loop parameters (debug and tests). */
  readonly cruise: Readonly<CruiseLoopParams>
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

/** The dev panel group "Reach audio": the cruise hum's gain, pitch and brightness range. */
function cruiseTuning(cfg: ReachAudioConfig): TuningGroup {
  const c = cfg.cruise
  const params: Record<string, TuningParam> = {
    gainMin: { value: c.gainMin, min: 0, max: 2, step: 0.01 },
    gainMax: { value: c.gainMax, min: 0, max: 2, step: 0.01 },
    rateMin: { value: c.rateMin, min: 0.25, max: 4, step: 0.01 },
    rateMax: { value: c.rateMax, min: 0.25, max: 4, step: 0.01 },
    cutoffMin: { value: c.cutoffMin, min: 50, max: 24000, step: 10 },
    cutoffMax: { value: c.cutoffMax, min: 50, max: 24000, step: 10 },
    laneGain: { value: c.laneGain, min: 0, max: 2, step: 0.01 },
  }
  return {
    name: 'Reach audio',
    params,
    apply(): void {
      c.gainMin = Number(params.gainMin.value)
      c.gainMax = Number(params.gainMax.value)
      c.rateMin = Number(params.rateMin.value)
      c.rateMax = Number(params.rateMax.value)
      c.cutoffMin = Number(params.cutoffMin.value)
      c.cutoffMax = Number(params.cutoffMax.value)
      c.laneGain = Number(params.laneGain.value)
    },
  }
}

export function createReachAudioFeature(source: AudioSource = audioFeature, config: ReachAudioConfig = reachAudioConfig): ReachAudioFeature {
  let router: ReachAudioRouter | null = null
  let lastMusic: MusicSystem | null = null
  let lastEngine: AudioEngine | null = null
  const cruise = createCruiseLoopParams()

  return {
    name: 'reachAudio',
    order: FEATURE_ORDER.reachAudio,
    config,
    cruise,

    init(ctx: GameContext): void {
      const core = ctx.reach
      if (core === null) return
      router = createReachAudioRouter(config)
      skipReachEvents(router, core.events)
      core.tuning.push(cruiseTuning(config))
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      const r = router
      const core = ctx.reach
      if (r === null || core === null) return
      const e = source.engine
      lastEngine = e
      if (e === null || f.paused) {
        skipReachEvents(r, core.events)
        return
      }
      const m = source.music
      if (m !== lastMusic) {
        // The score came up (or was rebuilt) in its default region: key it to the system we are in.
        lastMusic = m
        const layout = core.session.layout
        if (m !== null && layout !== null) keyScoreTo(core, layout.systemIndex, m)
      }
      routeReachEvents(r, core, e, m)
      cruiseLoop(core.travel, config.cruise, cruise)
      e.setLoop(LOOP.reachCruise, cruise.on, cruise.gain, cruise.rate, cruise.cutoff)
      if (m !== null && m.setFloor !== undefined) m.setFloor(musicFloorOf(core))
    },

    dispose(): void {
      if (lastEngine !== null) lastEngine.setLoop(LOOP.reachCruise, false, 0, 1, cruise.cutoff)
      router = null
      lastMusic = null
      lastEngine = null
      cruise.on = false
    },
  }
}

const feature: ReachAudioFeature = createReachAudioFeature()

export default feature
